// src/components/operationCenter/widgets/ThresholdMonitorWidget.tsx
// Threshold Monitor - Simple list showing alert policies and their current status

import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import type { WidgetProps, RiskAlertsConfig } from "@/types/dashboard";
import { useAlertPolicies, usePortfolioCurrent } from "@/hooks/portfolio";
import { useMetricsCatalog } from "@/hooks/useMetricsCatalog";
import { useAccessToken } from "@/hooks/useAccessToken";
import { CheckCircle, AlertTriangle, XCircle, Bell, Plus } from "lucide-react";
import type { AlertPolicy } from "@/types/risk";
import { resolveColumnLabel } from "@/utils/dashboard/metricHelpers";

type ThresholdStatus = "ok" | "warning" | "triggered";

interface ThresholdItem {
  policy: AlertPolicy;
  currentValue: number | null;
  status: ThresholdStatus;
}

const ThresholdMonitorWidget: React.FC<WidgetProps<RiskAlertsConfig>> = ({ widget }) => {
  const authToken = useAccessToken();

  const { data: policiesData, isLoading: policiesLoading } = useAlertPolicies(authToken);
  const { data: currentData, isLoading: currentLoading } = usePortfolioCurrent(authToken);
  const { data: metricsCatalog } = useMetricsCatalog();

  const isLoading = policiesLoading || currentLoading;

  const policies = useMemo(() => policiesData?.data ?? [], [policiesData?.data]);
  const currentMetrics = useMemo(() => currentData?.data ?? {}, [currentData?.data]);

  // Build minimal catalog for label resolution
  const minimalCatalog = useMemo(() => {
    return (metricsCatalog ?? []).map(m => ({
      metric_key: m.metric_key,
      display_name: m.display_name,
    }));
  }, [metricsCatalog]);

  const resolveMetricName = (key: string) => resolveColumnLabel(key, minimalCatalog);

  // Evaluate thresholds
  const thresholds = useMemo((): ThresholdItem[] => {
    return policies
      .filter(p => p.enabled !== false)
      .map(policy => {
        const currentValue = currentMetrics[policy.metric_key] ?? null;
        let status: ThresholdStatus = "ok";

        if (currentValue !== null && policy.threshold !== undefined) {
          const threshold = policy.threshold;
          const op = policy.operator;

          const isBreached =
            (op === ">" && currentValue > threshold) ||
            (op === ">=" && currentValue >= threshold) ||
            (op === "<" && currentValue < threshold) ||
            (op === "<=" && currentValue <= threshold);

          if (isBreached) {
            status = "triggered";
          } else {
            const warningThreshold = Math.abs(threshold * 0.2);
            const distanceToThreshold = Math.abs(currentValue - threshold);
            if (distanceToThreshold <= warningThreshold) {
              status = "warning";
            }
          }
        }

        return { policy, currentValue, status };
      })
      .sort((a, b) => {
        const statusOrder = { triggered: 0, warning: 1, ok: 2 };
        return statusOrder[a.status] - statusOrder[b.status];
      });
  }, [policies, currentMetrics]);

  const formatValue = (value: number | null | undefined, metricKey: string) => {
    if (value === null || value === undefined) return '-';
    const key = metricKey.toLowerCase();
    if (key.includes('pct') || key.includes('ret') || key.includes('vol') || key.includes('drawdown')) {
      return `${(value * 100).toFixed(1)}%`;
    }
    if (key.includes('sharpe') || key.includes('sortino') || key.includes('beta') || key.includes('corr')) {
      return value.toFixed(2);
    }
    if (key.includes('hhi')) {
      return value.toFixed(3);
    }
    return value.toFixed(2);
  };

  const formatThreshold = (policy: AlertPolicy) => {
    const { operator, threshold, metric_key } = policy;
    const formattedThreshold = formatValue(threshold, metric_key);
    return `${operator} ${formattedThreshold}`;
  };

  const getScopeLabel = (policy: AlertPolicy) => {
    if (policy.entity_type === 'portfolio') return 'Portfolio';
    if (policy.entity_type === 'token' && policy.entity_id) return policy.entity_id;
    if (policy.entity_type === 'category' && policy.entity_id) return policy.entity_id;
    return policy.entity_type;
  };

  const getStatusIcon = (status: ThresholdStatus) => {
    switch (status) {
      case "ok": return <CheckCircle className="w-3 h-3 text-emerald-400" />;
      case "warning": return <AlertTriangle className="w-3 h-3 text-amber-400" />;
      case "triggered": return <XCircle className="w-3 h-3 text-red-400" />;
    }
  };

  const triggeredCount = thresholds.filter(t => t.status === 'triggered').length;
  const warningCount = thresholds.filter(t => t.status === 'warning').length;

  if (isLoading) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center px-3 py-2 border-b border-slate-700/30">
          <div className="animate-pulse h-3 w-24 bg-slate-700/50 rounded" />
        </div>
        <div className="flex-1 p-2">
          <div className="animate-pulse space-y-1">
            {[0, 1, 2, 3, 4].map(i => (
              <div key={i} className="h-6 bg-slate-700/30 rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/30 flex-shrink-0">
        <div className="flex items-center gap-2">
          {triggeredCount > 0 && <div className="w-2 h-2 rounded-full bg-red-400 animate-pulse" />}
          <span className="text-xs uppercase tracking-wide text-slate-300 font-medium">
            {widget.title ?? "Threshold Monitor"}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[10px]">
          {triggeredCount > 0 && (
            <span className="text-red-400">{triggeredCount} triggered</span>
          )}
          {warningCount > 0 && (
            <span className="text-amber-400">{warningCount} warning</span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto min-h-0">
        {thresholds.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-400 text-sm">
            <Bell className="w-6 h-6 mb-2 opacity-50" />
            <span className="text-xs">No policies configured</span>
          </div>
        ) : (
          <div className="divide-y divide-slate-700/20">
            {thresholds.map((item, idx) => (
              <div
                key={item.policy.id}
                className={`flex items-center justify-between px-3 py-1.5 ${idx % 2 === 0 ? 'bg-slate-800/20' : ''}`}
              >
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  {getStatusIcon(item.status)}
                  <span className="text-[11px] text-slate-200 truncate">
                    {resolveMetricName(item.policy.metric_key)}
                  </span>
                  <span className={`px-1 py-0.5 rounded text-[8px] font-medium ${
                    item.policy.entity_type === 'portfolio'
                      ? 'bg-purple-500/20 text-purple-300'
                      : item.policy.entity_type === 'token'
                      ? 'bg-blue-500/20 text-blue-300'
                      : 'bg-teal-500/20 text-teal-300'
                  }`}>
                    {getScopeLabel(item.policy)}
                  </span>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0 ml-2">
                  <span className="text-[11px] font-semibold text-slate-200 tabular-nums">
                    {formatValue(item.currentValue, item.policy.metric_key)}
                  </span>
                  <span className="text-[10px] text-slate-500 tabular-nums">
                    {formatThreshold(item.policy)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-3 py-1.5 border-t border-slate-700/30 bg-slate-800/20 flex-shrink-0">
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-slate-500">{thresholds.length} policies</span>
          <Link
            to="/app/portfolio/alerts"
            className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium text-blue-400 hover:text-blue-300 hover:bg-blue-500/10 transition-colors"
          >
            <Plus className="w-3 h-3" />
            Add/Edit Alerts
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ThresholdMonitorWidget;
